/**
 * Border Radius Tokens
 *
 * Consistent corner rounding scale for the design system.
 * Supports responsive values with mobile and desktop scales.
 *
 * @see DS-003-border-radius.md
 * @figma https://www.figma.com/design/NXGWL5ErhbsA0kqoybqQbg/Design-System?node-id=220-9054
 */

/**
 * Border radius scale (mobile-first)
 */
export const borderRadius = {
  none: 0,
  xs: 2,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  "2xl": 24,
  full: 9999,
} as const;

/**
 * Desktop border radius overrides
 */
export const borderRadiusDesktop = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  "2xl": 32,
  full: 9999,
} as const;

// Utility functions

/**
 * Get border radius value based on platform
 */
export const getBorderRadius = (key: BorderRadiusKey, isDesktop = false): number => {
  return isDesktop ? borderRadiusDesktop[key] : borderRadius[key];
};

/**
 * Get border radius for specific corners
 * Useful for asymmetric rounding (e.g., cards attached to edges)
 */
export const getCornerRadius = (
  corners: {
    topLeft?: BorderRadiusKey;
    topRight?: BorderRadiusKey;
    bottomLeft?: BorderRadiusKey;
    bottomRight?: BorderRadiusKey;
  },
  isDesktop = false,
) => {
  const radiusMap = isDesktop ? borderRadiusDesktop : borderRadius;
  return {
    borderTopLeftRadius: corners.topLeft ? radiusMap[corners.topLeft] : 0,
    borderTopRightRadius: corners.topRight ? radiusMap[corners.topRight] : 0,
    borderBottomLeftRadius: corners.bottomLeft ? radiusMap[corners.bottomLeft] : 0,
    borderBottomRightRadius: corners.bottomRight ? radiusMap[corners.bottomRight] : 0,
  };
};

// Type exports
export type BorderRadiusKey = keyof typeof borderRadius;
